import { type FocusEvent, type KeyboardEvent, type RefObject, useCallback, useEffect, useRef } from "react";

export const FIRST_CONTROL = "input:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex='-1'])";

export const focusFirstControl = (event: FocusEvent<HTMLDivElement>) => {
  if (event.target !== event.currentTarget) return;
  event.currentTarget.querySelector<HTMLElement>(FIRST_CONTROL)?.focus();
};

const FRAMES_TO_WAIT_FOR_OPTIONS = 30;
const FRAMES_TO_KEEP_WAITING_ON_THE_MENU = 600;

const focusLands = (element: HTMLElement) => {
  element.focus();
  return document.activeElement === element;
};

export const useFocusIntoMenuOnEveryOpening = (list: RefObject<HTMLDivElement>) => {
  const pendingFrame = useRef(0);
  const stop = useCallback(() => cancelAnimationFrame(pendingFrame.current), []);
  const start = useCallback(() => {
    stop();
    const attempt = (frame: number, menuHoldsFocus: boolean) => {
      pendingFrame.current = requestAnimationFrame(() => {
        const menu = list.current;
        if (menuHoldsFocus && document.activeElement !== menu) return;
        const first = menu?.querySelector<HTMLElement>(FIRST_CONTROL);
        if (first && focusLands(first)) return;
        const holds = menuHoldsFocus || (frame >= FRAMES_TO_WAIT_FOR_OPTIONS && !!menu && focusLands(menu));
        if (frame < FRAMES_TO_WAIT_FOR_OPTIONS + FRAMES_TO_KEEP_WAITING_ON_THE_MENU) attempt(frame + 1, holds);
      });
    };
    attempt(0, false);
  }, [list, stop]);
  useEffect(() => stop, [stop]);
  return { start, stop };
};

export const fromTheMenuToItsFirstControl = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.target !== event.currentTarget) return;
  if (event.key !== "Tab" && event.key !== "ArrowDown") return;
  const first = event.currentTarget.querySelector<HTMLElement>(FIRST_CONTROL);
  if (!first) return;
  event.preventDefault();
  first.focus();
};

const STEPS_WITH_ARROWS = "input[type='checkbox'], input[type='radio'], button";

export const moveBetweenControls = (close: () => void) => (event: KeyboardEvent<HTMLDivElement>) => {
  const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(FIRST_CONTROL));
  const at = controls.indexOf(event.target as HTMLElement);
  if (event.key === "Tab") {
    event.stopPropagation();
    const leaving = event.shiftKey ? at <= 0 : at === controls.length - 1;
    if (leaving) {
      event.preventDefault();
      close();
      return;
    }
    const menu = event.currentTarget;
    requestAnimationFrame(() => {
      if (!menu.contains(document.activeElement)) close();
    });
    return;
  }
  if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
  event.stopPropagation();
  if (!(event.target as HTMLElement).matches(STEPS_WITH_ARROWS)) return;
  event.preventDefault();
  controls[at + (event.key === "ArrowDown" ? 1 : -1)]?.focus();
};

export const keepFocusIn = (list: RefObject<HTMLDivElement>, preferred: string | null, frames = 30): number =>
  requestAnimationFrame(() => {
    const menu = list.current;
    if (!menu) return;
    const wanted = preferred ? menu.querySelector<HTMLElement>(preferred) : null;
    if (wanted) {
      wanted.focus();
      return;
    }
    const lost = !menu.contains(document.activeElement) || document.activeElement === menu;
    if (lost && !preferred) {
      menu.querySelector<HTMLElement>(FIRST_CONTROL)?.focus();
      return;
    }
    if (frames > 0) {
      keepFocusIn(list, preferred, frames - 1);
      return;
    }
    if (lost) menu.querySelector<HTMLElement>(FIRST_CONTROL)?.focus();
  });
